import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Button } from '@/components/atoms';
import { formatarData } from '@/i18n/formato';
import { providersApi } from '@/services/config';
import { metricsApi } from '@/services/metrics';

import styles from './ConfigPublicacao.module.css';

/**
 * Política de publicação — as travas do MOTOR num lugar só.
 *
 * Cada regra aqui tem base na documentação do Google, e a tela diz qual: sem
 * isso o operador não tem como julgar se ligar ou não. As três famílias:
 *
 *  - guardas de escala (política de conteúdo em escala: "muitas páginas
 *    geradas sem agregar valor")
 *  - divulgação de automação (o Google pede transparência sobre uso de IA)
 *  - portão do Discover (largura mínima de imagem) e feriados (o que "dia
 *    útil" significa para as esteiras)
 */

const PADRAO = {
  piso_demanda: 0,
  teto_assunto_dia: 3,
  divulgacao: 'nenhum',
  divulgacao_texto:
    'Esta matéria foi produzida com apoio de automação e revisada pela redação.',
  capa_min_width: 1200,
  capa_exigir: false,
  feriados_extras: {} as Record<string, string>,
  trava_concorrente: true,
  // undefined = nunca gravado: a tela mostra a lista que o motor está usando (a padrão)
  marcas_concorrentes: undefined as string[] | undefined,
};

const DIAS = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'];

export function ConfigPublicacao() {
  const { t } = useTranslation(['config', 'common']);
  const qc = useQueryClient();
  const [cfg, setCfg] = useState(PADRAO);
  const [dirty, setDirty] = useState(false);
  const [feriadoData, setFeriadoData] = useState('');
  const [feriadoNome, setFeriadoNome] = useState('');
  const [calendario, setCalendario] = useState<'nacional' | 'mercado'>('nacional');
  // texto cru da lista de veículos: filtrar linha vazia a cada tecla impediria abrir linha nova
  const [marcasTexto, setMarcasTexto] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['provider', 'politica_publicacao'],
    queryFn: async () => {
      try {
        return await providersApi.get('politica_publicacao');
      } catch {
        return null; // nunca configurado: usa os padrões
      }
    },
  });

  useEffect(() => {
    const c = (data?.config ?? {}) as Partial<typeof PADRAO>;
    setCfg({ ...PADRAO, ...c, feriados_extras: c.feriados_extras ?? {} });
    setMarcasTexto(null);
    setDirty(false);
  }, [data]);

  const feriados = useQuery({
    queryKey: ['feriados', calendario],
    queryFn: () => metricsApi.feriados(calendario),
  });

  const trava = useQuery({
    queryKey: ['trava-concorrente'],
    queryFn: () => metricsApi.travaConcorrente(7),
  });
  const marcas = cfg.marcas_concorrentes ?? trava.data?.marcas ?? [];
  const quedas = trava.data?.ultimas ?? [];

  const salvar = useMutation({
    mutationFn: () => providersApi.upsert('politica_publicacao', cfg),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['provider', 'politica_publicacao'] });
      qc.invalidateQueries({ queryKey: ['feriados'] });
      qc.invalidateQueries({ queryKey: ['trava-concorrente'] });
    },
  });

  const set = <K extends keyof typeof PADRAO>(k: K, v: (typeof PADRAO)[K]) => {
    setCfg((c) => ({ ...c, [k]: v }));
    setDirty(true);
  };

  const addFeriado = () => {
    if (!feriadoData || !feriadoNome.trim()) return;
    set('feriados_extras', { ...cfg.feriados_extras, [feriadoData]: feriadoNome.trim() });
    setFeriadoData('');
    setFeriadoNome('');
  };

  const removeFeriado = (iso: string) => {
    const next = { ...cfg.feriados_extras };
    delete next[iso];
    set('feriados_extras', next);
  };

  if (isLoading) return <p className={styles.muted}>Carregando…</p>;

  const hojeInfo = feriados.data;

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>{t('config:publishing_policy.title')}</h1>
          <p className={styles.subtitle}>
            As travas que valem para <strong>todos</strong> os caminhos de produção —
            esteira automática, mesa, editorias e fábricas. Cada regra abaixo diz em que
            orientação do Google se apoia.
          </p>
        </div>
        <Button onClick={() => salvar.mutate()} disabled={!dirty} loading={salvar.isPending}>
          Guardar
        </Button>
      </header>

      {salvar.isSuccess && !dirty && <p className={styles.ok}>Guardado.</p>}
      {salvar.isError && <p className={styles.erro}>Falha ao salvar.</p>}

      {/* ─── Trava de concorrente ─── */}
      <div className={styles.bloco}>
        <h2 className={styles.blocoTitle}>Trava de concorrente</h2>
        <p className={styles.blocoDesc}>
          Matéria de <strong>esteira automática</strong> (Quentes, editorias, fábricas,
          vigias) que cite outro veículo de imprensa como fonte — na manchete, na linha
          fina, no corpo ou no título SEO — <strong>não é publicada</strong>. A pauta fica
          como falha no quadro de Produção, com o motivo. Emissora de TV como assunto
          (novela, transmissão de jogo, debate) não conta. A mesa da Inteligência não passa
          por aqui: lá uma pessoa escolhe a pauta.
        </p>

        <label className={styles.radio}>
          <input
            type="checkbox"
            checked={cfg.trava_concorrente}
            onChange={(e) => set('trava_concorrente', e.target.checked)}
          />
          Trava ligada
        </label>

        <div className={styles.hojeBox}>
          <Badge tone={trava.data?.ligada === false ? 'warn' : 'ok'} size="sm">
            {trava.data?.ligada === false ? 'desligada' : 'ligada'}
          </Badge>
          <span className={styles.hint}>
            {trava.isLoading
              ? 'Carregando…'
              : trava.isError
                ? 'Não foi possível ler as quedas agora.'
                : trava.data?.quedas
                  ? `${trava.data.quedas} matéria(s) automática(s) não publicada(s) nos últimos ${trava.data.dias} dias`
                  : `Nenhuma matéria derrubada nos últimos ${trava.data?.dias ?? 7} dias.`}
          </span>
        </div>
        {quedas.length > 0 && (
          <ul className={styles.feriados}>
            {quedas.map((q, i) => (
              <li key={`${q.em}-${i}`} className={styles.feriadoRow}>
                <span className={styles.feriadoData}>
                  {q.em ? formatarData(new Date(q.em)) : '—'}
                </span>
                <span className={styles.feriadoNome}>{q.titulo}</span>
                <Badge tone="warn" size="xs">
                  “{q.marca}” na {q.onde}
                </Badge>
              </li>
            ))}
          </ul>
        )}

        <label className={styles.campo}>
          <span className={styles.label}>Veículos (um por linha)</span>
          <textarea
            value={marcasTexto ?? marcas.join('\n')}
            onChange={(e) => {
              setMarcasTexto(e.target.value);
              set(
                'marcas_concorrentes',
                e.target.value.split('\n').map((m) => m.trim()).filter(Boolean),
              );
            }}
            className={styles.textarea}
            rows={8}
          />
          <span className={styles.ajuda}>
            Escreva como aparece no texto; acento e maiúscula não importam. Acrescente o
            concorrente da sua praça. Tire daqui a rede da qual a sua emissora é afiliada.
            Lista vazia volta para a padrão — para parar a trava, use o botão acima.
          </span>
        </label>
      </div>

      {/* ─── Guardas de escala ─── */}
      <div className={styles.bloco}>
        <h2 className={styles.blocoTitle}>Guardas de escala</h2>
        <p className={styles.blocoDesc}>
          O Google define abuso de conteúdo em escala como “muitas páginas geradas com o
          propósito principal de manipular ranking e não de ajudar usuários”, citando IA
          generativa nominalmente. O método não é o problema — o valor é. Estas duas
          travas existem para o motor nunca cair nessa definição.
        </p>

        <label className={styles.campo}>
          <span className={styles.label}>Piso de demanda</span>
          <div className={styles.inline}>
            <input
              type="number"
              min={0}
              step={100}
              value={cfg.piso_demanda}
              onChange={(e) => set('piso_demanda', Number(e.target.value))}
              className={styles.input}
            />
            <span className={styles.hint}>buscas mínimas — 0 desliga</span>
          </div>
          <span className={styles.ajuda}>
            Assunto com volume de busca abaixo disto não vira matéria. No incidente de
            julho o painel congelado gerou pautas de volume 200 a 5.000; um piso teria
            barrado a maior parte. Horóscopo, loteria, agenda e editorias são{' '}
            <strong>isentos</strong> — o valor delas não vem de volume de tendência.
          </span>
          <span className={styles.aviso}>
            Não confundir com o <Link to="/config/quentes">corte de score do Fluxo de
            Quentes</Link>: aquele escolhe <em>quais</em> tendências a esteira automática
            pega; este barra <em>qualquer</em> caminho de produção, inclusive o manual.
          </span>
        </label>

        <label className={styles.campo}>
          <span className={styles.label}>Cadência padrão do Vigia</span>
          <div className={styles.inline}>
            <input
              type="number"
              min={0}
              max={20}
              value={cfg.teto_assunto_dia}
              onChange={(e) => set('teto_assunto_dia', Number(e.target.value))}
              className={styles.input}
            />
            <span className={styles.hint}>pautas por dia, por documento</span>
          </div>
          <span className={styles.ajuda}>
            Quando um Vigia abre várias pautas a partir do mesmo documento, quantas ele
            espalha por dia. Cada Vigia pode ter a sua; este é o padrão da casa.
            <br />
            <strong>Não é limite de publicação:</strong> notícia não tem teto por
            assunto. Se a redação quiser 30 matérias sobre o mesmo tema num dia, pode —
            elas aparecem agrupadas por cor no quadro de Produção.
          </span>
        </label>
      </div>

      {/* ─── Divulgação de automação ─── */}
      <div className={styles.bloco}>
        <h2 className={styles.blocoTitle}>Divulgação de automação</h2>
        <p className={styles.blocoDesc}>
          O Google pede transparência explícita: no framework Quem/Como/Porquê, “Como:
          divulgue o uso de automação/IA de forma transparente”.{' '}
          <strong>Decisão editorial</strong> — vale alinhar com a direção antes de ligar.
        </p>
        <div className={styles.radios}>
          {[
            { v: 'nenhum', l: 'Não divulgar' },
            { v: 'rodape', l: 'Nota no fim da matéria' },
          ].map((o) => (
            <label key={o.v} className={styles.radio}>
              <input
                type="radio"
                name="divulgacao"
                checked={cfg.divulgacao === o.v}
                onChange={() => set('divulgacao', o.v)}
              />
              {o.l}
            </label>
          ))}
        </div>
        {cfg.divulgacao === 'rodape' && (
          <label className={styles.campo}>
            <span className={styles.label}>Texto da nota</span>
            <textarea
              value={cfg.divulgacao_texto}
              onChange={(e) => set('divulgacao_texto', e.target.value)}
              className={styles.textarea}
              rows={2}
            />
          </label>
        )}
      </div>

      {/* ─── Portão do Discover ─── */}
      <div className={styles.bloco}>
        <h2 className={styles.blocoTitle}>Portão do Discover</h2>
        <p className={styles.blocoDesc}>
          O Discover exige imagem com largura mínima de 1200px (e{' '}
          <code>max-image-preview:large</code>). Medição no cliente #1: só 39-55% das
          matérias qualificavam — metade nunca teve chance de aparecer.
        </p>
        <label className={styles.campo}>
          <span className={styles.label}>Largura mínima da capa</span>
          <div className={styles.inline}>
            <input
              type="number"
              min={0}
              step={100}
              value={cfg.capa_min_width}
              onChange={(e) => set('capa_min_width', Number(e.target.value))}
              className={styles.input}
            />
            <span className={styles.hint}>pixels</span>
          </div>
          <span className={styles.ajuda}>
            O motor procura primeiro uma capa que atenda a este mínimo; se nenhuma fonte
            tiver, aceita a menor (capa pequena é melhor que nenhuma).
          </span>
        </label>
      </div>

      {/* ─── Feriados ─── */}
      <div className={styles.bloco}>
        <h2 className={styles.blocoTitle}>Feriados</h2>
        <p className={styles.blocoDesc}>
          É o que define <strong>“dia útil”</strong> para as esteiras. Sem isto,{' '}
          <code>6-9h úteis</code> rodaria numa terça de Carnaval e publicaria abertura da
          B3 com a bolsa fechada. O calendário nacional é automático (os móveis saem da
          Páscoa); o local é seu.
        </p>

        {hojeInfo && (
          <div className={styles.hojeBox}>
            <Badge tone={hojeInfo.hoje_e_util ? 'ok' : 'warn'} size="sm">
              {hojeInfo.hoje_e_util ? 'hoje é dia útil' : 'hoje NÃO é dia útil'}
            </Badge>
            {!hojeInfo.hoje_e_util && (
              <span className={styles.hint}>
                próximo dia útil:{' '}
                {formatarData(new Date(`${hojeInfo.proximo_dia_util}T12:00:00`))}
              </span>
            )}
          </div>
        )}

        <div className={styles.radios}>
          {[
            { v: 'nacional', l: 'Calendário nacional' },
            { v: 'mercado', l: 'Calendário da bolsa (B3)' },
          ].map((o) => (
            <label key={o.v} className={styles.radio}>
              <input
                type="radio"
                name="calendario"
                checked={calendario === o.v}
                onChange={() => setCalendario(o.v as 'nacional' | 'mercado')}
              />
              {o.l}
            </label>
          ))}
        </div>

        <ul className={styles.feriados}>
          {(feriados.data?.itens ?? []).map((f) => (
            <li key={`${f.data}-${f.nome}`} className={styles.feriadoRow}>
              <span className={styles.feriadoData}>
                {formatarData(new Date(`${f.data}T12:00:00`))} ·{' '}
                {DIAS[f.dia_semana]}
              </span>
              <span className={styles.feriadoNome}>{f.nome}</span>
              <Badge tone={f.origem === 'local' ? 'info' : 'neutral'} size="xs">
                {f.origem === 'local' ? 'local' : f.origem === 'mercado' ? 'B3' : 'nacional'}
              </Badge>
              {f.origem === 'local' && (
                <button className={styles.remover} onClick={() => removeFeriado(f.data)}>
                  remover
                </button>
              )}
            </li>
          ))}
        </ul>

        <div className={styles.addFeriado}>
          <input
            type="date"
            value={feriadoData}
            onChange={(e) => setFeriadoData(e.target.value)}
            className={styles.input}
          />
          <input
            type="text"
            placeholder="Nome do feriado local (ex.: São João)"
            value={feriadoNome}
            onChange={(e) => setFeriadoNome(e.target.value)}
            className={styles.inputWide}
          />
          <Button size="sm" variant="secondary" onClick={addFeriado}>
            Acrescentar
          </Button>
        </div>
        <span className={styles.ajuda}>
          Feriado municipal ou estadual. O nacional é igual para todo cliente; o local
          não — o feriado de Recife não é o de Manaus.
        </span>
      </div>

      {/* ─── Vocabulário das janelas ─── */}
      <div className={styles.bloco}>
        <h2 className={styles.blocoTitle}>Vocabulário das janelas de disparo</h2>
        <p className={styles.blocoDesc}>
          O que escrever no campo <em>intervalo</em> de cada{' '}
          <Link to="/editorias">editoria</Link>.
        </p>
        <table className={styles.tabela}>
          <tbody>
            {[
              ['6-9h úteis', 'segunda a sexta, PULANDO feriado nacional'],
              ['6-9h b3', 'dias em que a bolsa negocia (pula também segunda de Carnaval, 24/12 e 31/12)'],
              ['10-12h sex', 'só às sextas-feiras'],
              ['18-22h qua,dom', 'quartas e domingos'],
              ['8-10h feriado', 'SÓ em feriado (esteira de conteúdo de feriado)'],
              ['6-9h corridos', 'todo dia, feriado inclusive (plantão, horóscopo)'],
              ['15min · 1h · 8h · diário', 'frequência, sem janela de horário'],
            ].map(([k, v]) => (
              <tr key={k}>
                <td className={styles.tabelaCod}>
                  <code>{k}</code>
                </td>
                <td className={styles.tabelaTxt}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default ConfigPublicacao;
